import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap, map, finalize, shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface LoginResponse {
  success: boolean;
  data: { username: string; role: string; mustChangePassword?: boolean };
  message: string;
}

/**
 * AA-32: el access token y el refresh token ya NO viajan en el body de login/refresh ni se
 * guardan en localStorage (localStorage es legible por cualquier XSS). El backend los setea como
 * cookies httpOnly/Secure/SameSite=Strict (ver AuthController) -- el navegador las adjunta solo a
 * este origen y JavaScript nunca puede leerlas. `withCredentials: true` en cada llamada es lo que
 * hace que el navegador mande/reciba esas cookies.
 *
 * `USER_KEY` NO es una credencial -- es solo username/role para que la UI no parpadee antes de la
 * primera respuesta del servidor. La autorización real siempre la decide el backend por cookie en
 * cada request; si la cookie expiró o es inválida, cualquier llamada responde 401 y el
 * interceptor (ver jwt.interceptor.ts) cierra la sesión del lado del cliente.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly USER_KEY = 'auth_user';
  isLoggedIn = signal(this.hasStoredUser());

  /** AA-68: coordina un único refresh en vuelo -- si varias peticiones reciben 401 al mismo
   *  tiempo, todas esperan el mismo refresh en vez de disparar varios refresh en paralelo. */
  private refreshInFlight$: Observable<void> | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/api/auth/login`, { username, password }, { withCredentials: true })
      .pipe(
        tap(res => {
          if (res.success) {
            this.storeUser(res.data);
            this.isLoggedIn.set(true);
          }
        })
      );
  }

  /** AA-68: renueva el access token en silencio con el refresh de larga duración (cookie
   *  httpOnly, el navegador la manda sola) -- es lo que mantiene la sesión viva mientras hay uso
   *  activo, sin pedir contraseña de nuevo. */
  refreshAccessToken(): Observable<void> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    this.refreshInFlight$ = this.http
      .post<LoginResponse>(`${environment.apiUrl}/api/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap(res => this.storeUser(res.data)),
        map(() => void 0),
        shareReplay(1),
        finalize(() => { this.refreshInFlight$ = null; })
      );
    return this.refreshInFlight$;
  }

  logout(): void {
    // best-effort: si falla (red caída, cookie ya vencida) no bloquea el logout local.
    this.http.post(`${environment.apiUrl}/api/auth/logout`, {}, { withCredentials: true })
      .subscribe({ error: () => {} });
    localStorage.removeItem(this.USER_KEY);
    this.isLoggedIn.set(false);
    this.router.navigate(['/login']);
  }

  getUser(): { username: string; role: string } | null {
    const u = localStorage.getItem(this.USER_KEY);
    return u ? JSON.parse(u) : null;
  }

  private storeUser(data: LoginResponse['data']): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(data));
  }

  private hasStoredUser(): boolean {
    return !!localStorage.getItem(this.USER_KEY);
  }
}
